import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, API_BASE, uploadFile } from '../api/client';

interface CatalogItem {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  imageUrl: string | null;
  category: string | null;
  tags: string | null;
  featured: boolean;
}

interface EventReg {
  boothNumber: string | null;
  zone: string | null;
  description: string | null;
  event: { name: string };
}

interface StagedEdit {
  id: string;
  editType: string;
  catalogItemId: string | null;
  payload: string;
  status: string;
  createdAt: string;
  rejectionReason: string | null;
}

interface VendorData {
  id: string;
  businessName: string;
  contactPerson: string;
  email: string;
  phone: string | null;
  category: string | null;
  catalogItems: CatalogItem[];
  eventRegistrations: EventReg[];
  pendingEdits: number;
}

type SubView = 'info' | 'catalog' | 'history';

export function MyVendorSpacePage() {
  const navigate = useNavigate();
  const [vendor, setVendor] = useState<VendorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState<SubView>('info');
  const [stagedEdits, setStagedEdits] = useState<StagedEdit[]>([]);

  // Add catalog item form
  const [showAddForm, setShowAddForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formImageUrl, setFormImageUrl] = useState('');
  const [formImageFile, setFormImageFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Edit vendor info form
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({ contactPerson: '', email: '', phone: '', category: '' });

  useEffect(() => {
    fetchVendor();
  }, []);

  const fetchVendor = async () => {
    setLoading(true);
    try {
      const data = await apiClient<VendorData>('/vendor-portal/my-vendor');
      setVendor(data);
      setInfoForm({
        contactPerson: data.contactPerson,
        email: data.email,
        phone: data.phone || '',
        category: data.category || '',
      });
    } catch (e: any) {
      setError(e.message || 'Unable to load vendor data');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const edits = await apiClient<StagedEdit[]>('/vendor-portal/my-vendor/staged-edits');
      setStagedEdits(edits);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (activeView === 'history') fetchHistory();
  }, [activeView]);

  const handleAddCatalogItem = async () => {
    if (!formName.trim()) return;
    setSubmitting(true);
    setSuccessMsg('');
    try {
      // Upload image first if one was selected
      let imageUrl = formImageUrl.trim() || null;
      if (formImageFile) {
        setUploadingImage(true);
        const uploadResult = await uploadFile<{ imageUrl: string }>('/vendor-portal/my-vendor/upload-image', formImageFile, 'image');
        imageUrl = uploadResult.imageUrl;
        setUploadingImage(false);
      }

      await apiClient('/vendor-portal/my-vendor/staged-edits', {
        method: 'POST',
        body: {
          editType: 'AddCatalogItem',
          payload: {
            name: formName.trim(),
            description: formDesc.trim() || null,
            category: formCategory.trim() || null,
            imageUrl,
          },
        },
      });
      setSuccessMsg('Catalog item submitted for organizer review!');
      setFormName(''); setFormDesc(''); setFormCategory(''); setFormImageUrl(''); setFormImageFile(null);
      setShowAddForm(false);
      if (vendor) setVendor({ ...vendor, pendingEdits: vendor.pendingEdits + 1 });
    } catch (e: any) {
      alert('Failed: ' + (e.message || 'Unknown error'));
      setUploadingImage(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCatalogItem = async (item: CatalogItem) => {
    const newName = prompt('Item name:', item.name);
    if (!newName || newName === item.name) return;
    const newDesc = prompt('Description:', item.description || '') || '';
    const newPrice = prompt('Price (0 or blank for free):', item.price?.toString() || '') || '';

    try {
      await apiClient('/vendor-portal/my-vendor/staged-edits', {
        method: 'POST',
        body: {
          editType: 'UpdateCatalogItem',
          catalogItemId: item.id,
          payload: {
            name: newName.trim(),
            description: newDesc.trim() || null,
            price: newPrice ? parseFloat(newPrice) : null,
          },
        },
      });
      setSuccessMsg('Edit submitted for review!');
      if (vendor) setVendor({ ...vendor, pendingEdits: vendor.pendingEdits + 1 });
    } catch (e: any) {
      alert('Failed: ' + (e.message || 'Unknown error'));
    }
  };

  const handleRemoveCatalogItem = async (item: CatalogItem) => {
    if (!confirm(`Remove "${item.name}" from your catalog? This requires organizer approval.`)) return;
    try {
      await apiClient('/vendor-portal/my-vendor/staged-edits', {
        method: 'POST',
        body: {
          editType: 'RemoveCatalogItem',
          catalogItemId: item.id,
          payload: { name: item.name, reason: 'Vendor requested removal' },
        },
      });
      setSuccessMsg('Removal request submitted for review.');
      if (vendor) setVendor({ ...vendor, pendingEdits: vendor.pendingEdits + 1 });
    } catch (e: any) {
      alert('Failed: ' + (e.message || 'Unknown error'));
    }
  };

  const handleUpdateInfo = async () => {
    setSubmitting(true);
    try {
      await apiClient('/vendor-portal/my-vendor/staged-edits', {
        method: 'POST',
        body: {
          editType: 'UpdateVendorInfo',
          payload: {
            contactPerson: infoForm.contactPerson.trim(),
            email: infoForm.email.trim(),
            phone: infoForm.phone.trim() || null,
            category: infoForm.category.trim() || null,
          },
        },
      });
      setSuccessMsg('Info update submitted for organizer review!');
      setEditingInfo(false);
      if (vendor) setVendor({ ...vendor, pendingEdits: vendor.pendingEdits + 1 });
    } catch (e: any) {
      alert('Failed: ' + (e.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingBox}>
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🏪</div>
          <p style={{ color: '#6b7280' }}>Loading your vendor space...</p>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div style={styles.page}>
        <div style={styles.errorBox}>
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>😕</div>
          <p style={{ color: '#dc2626', fontWeight: 500 }}>{error || 'No vendor account found'}</p>
          <button style={styles.backBtn} onClick={() => navigate('/my-weekend')}>← Back to My Weekend</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backLink} onClick={() => navigate('/my-weekend')}>← My Weekend</button>
        <h1 style={styles.title}>🏪 {vendor.businessName}</h1>
        <p style={styles.subtitle}>Manage your vendor profile and catalog items</p>
        {vendor.pendingEdits > 0 && (
          <div style={styles.pendingBanner}>
            ⏳ {vendor.pendingEdits} edit{vendor.pendingEdits > 1 ? 's' : ''} pending organizer review
          </div>
        )}
      </div>

      {/* Success message */}
      {successMsg && (
        <div style={styles.successBanner}>
          ✅ {successMsg}
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: '8px' }} onClick={() => setSuccessMsg('')}>✕</button>
        </div>
      )}

      {/* Sub-nav */}
      <nav style={styles.subNav} aria-label="Vendor sections">
        {([
          { key: 'info', label: 'My Info' },
          { key: 'catalog', label: 'Catalog Items' },
          { key: 'history', label: 'Edit History' },
        ] as { key: SubView; label: string }[]).map(v => (
          <button
            key={v.key}
            style={activeView === v.key ? styles.tabActive : styles.tab}
            onClick={() => setActiveView(v.key)}
            aria-current={activeView === v.key ? 'page' : undefined}
          >
            {v.label}
          </button>
        ))}
      </nav>

      {/* ── INFO VIEW ─────────────────────── */}
      {activeView === 'info' && (
        <div style={styles.section}>
          {!editingInfo ? (
            <div style={styles.infoCard}>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Business:</span><span>{vendor.businessName}</span></div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Contact:</span><span>{vendor.contactPerson}</span></div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Email:</span><span>{vendor.email}</span></div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Phone:</span><span>{vendor.phone || '—'}</span></div>
              <div style={styles.infoRow}><span style={styles.infoLabel}>Category:</span><span>{vendor.category || '—'}</span></div>

              {vendor.eventRegistrations.length > 0 && (
                <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: '8px' }}>Event Assignments</div>
                  {vendor.eventRegistrations.map((er, i) => (
                    <div key={i} style={{ fontSize: '0.8rem', marginBottom: '6px', padding: '6px 10px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                      <div style={{ fontWeight: 500 }}>{er.event.name}</div>
                      {(er.boothNumber || er.zone) && (
                        <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                          {er.boothNumber && `Booth: ${er.boothNumber}`}{er.boothNumber && er.zone && ' • '}{er.zone && `Zone: ${er.zone}`}
                        </div>
                      )}
                      {er.description && <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>{er.description}</div>}
                    </div>
                  ))}
                </div>
              )}

              <button style={styles.editInfoBtn} onClick={() => setEditingInfo(true)}>
                ✏️ Request Info Update
              </button>
            </div>
          ) : (
            <div style={styles.infoCard}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Contact Person</label>
                <input style={styles.input} value={infoForm.contactPerson} onChange={e => setInfoForm({ ...infoForm, contactPerson: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input style={styles.input} type="email" value={infoForm.email} onChange={e => setInfoForm({ ...infoForm, email: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input style={styles.input} type="tel" value={infoForm.phone} onChange={e => setInfoForm({ ...infoForm, phone: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category</label>
                <input style={styles.input} value={infoForm.category} onChange={e => setInfoForm({ ...infoForm, category: e.target.value })} />
              </div>
              <p style={{ fontSize: '0.72rem', color: '#6b7280', margin: '8px 0' }}>Changes require organizer approval before taking effect.</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={styles.submitBtn} onClick={handleUpdateInfo} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit for Review'}
                </button>
                <button style={styles.cancelBtn} onClick={() => setEditingInfo(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── CATALOG VIEW ──────────────────── */}
      {activeView === 'catalog' && (
        <div style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827' }}>
              Your Catalog ({vendor.catalogItems.length} items)
            </h3>
            <button style={styles.addBtn} onClick={() => setShowAddForm(!showAddForm)}>
              {showAddForm ? '✕ Cancel' : '+ Add Item'}
            </button>
          </div>

          {/* Add form */}
          {showAddForm && (
            <div style={styles.addFormCard}>
              <div style={{ padding: '10px 12px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', marginBottom: '12px' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e40af', marginBottom: '4px' }}>How to add a catalog item</div>
                <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '0.72rem', color: '#374151', lineHeight: 1.6 }}>
                  <li>Enter the item name and a short description</li>
                  <li>Upload a clear photo (JPEG, PNG, or WebP, max 2MB)</li>
                  <li>Select a category to help guests browse</li>
                  <li>All submissions are reviewed by an organizer before going live</li>
                </ul>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Item Name *</label>
                <input style={styles.input} value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g., Handmade Bracelet" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }} value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder="Brief description of your item..." />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category</label>
                <input style={styles.input} value={formCategory} onChange={e => setFormCategory(e.target.value)} placeholder="e.g., Jewelry, Food, Clothing" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Product Image (max 2MB)</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 2 * 1024 * 1024) {
                        alert('Image must be under 2MB. Please choose a smaller file or compress it.');
                        e.target.value = '';
                        return;
                      }
                      setFormImageFile(file);
                      setFormImageUrl('');
                    }
                  }}
                  style={{ fontSize: '0.82rem' }}
                />
                {formImageFile && (
                  <div style={{ marginTop: '6px', fontSize: '0.72rem', color: '#166534' }}>
                    Selected: {formImageFile.name} ({(formImageFile.size / 1024).toFixed(0)} KB)
                  </div>
                )}
                <div style={{ marginTop: '4px', fontSize: '0.68rem', color: '#6b7280' }}>
                  Accepted: JPEG, PNG, WebP, GIF. Keep under 2MB for best results.
                </div>
              </div>
              <p style={{ fontSize: '0.72rem', color: '#6b7280', margin: '6px 0' }}>All items are reviewed by an organizer before they appear on the Guest Pass and Vendor Showcase.</p>
              <button style={styles.submitBtn} onClick={handleAddCatalogItem} disabled={submitting || uploadingImage || !formName.trim()}>
                {uploadingImage ? 'Uploading image...' : submitting ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          )}

          {/* Existing items */}
          {vendor.catalogItems.length === 0 && !showAddForm && (
            <p style={{ color: '#6b7280', fontSize: '0.88rem', textAlign: 'center', padding: '30px 0' }}>
              No catalog items yet. Tap "+ Add Item" to add your products or services.
            </p>
          )}

          <div style={styles.catalogGrid}>
            {vendor.catalogItems.map(item => (
              <div key={item.id} style={styles.catalogCard}>
                {item.imageUrl && (
                  <div style={styles.catalogImg}>
                    <img src={item.imageUrl.startsWith('http') ? item.imageUrl : `${API_BASE}${item.imageUrl}`} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <div style={styles.catalogBody}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {item.featured && '⭐ '}{item.name}
                  </div>
                  {item.description && <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>{item.description}</div>}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
                    {item.price != null && Number(item.price) > 0 && (
                      <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: '0.82rem' }}>${Number(item.price).toFixed(2)}</span>
                    )}
                    {item.category && <span style={{ fontSize: '0.68rem', padding: '1px 6px', backgroundColor: '#f3f4f6', borderRadius: '4px', color: '#6b7280' }}>{item.category}</span>}
                  </div>
                </div>
                <div style={styles.catalogActions}>
                  <button style={styles.iconBtn} onClick={() => handleEditCatalogItem(item)} title="Edit" aria-label={`Edit ${item.name}`}>✏️</button>
                  <button style={styles.iconBtn} onClick={() => handleRemoveCatalogItem(item)} title="Remove" aria-label={`Remove ${item.name}`}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── HISTORY VIEW ──────────────────── */}
      {activeView === 'history' && (
        <div style={styles.section}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600, color: '#111827' }}>Edit History</h3>
          {stagedEdits.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '0.88rem', textAlign: 'center', padding: '30px 0' }}>No edits submitted yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {stagedEdits.map(edit => (
                <div key={edit.id} style={styles.historyRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 500 }}>{formatEditType(edit.editType)}</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>{new Date(edit.createdAt).toLocaleDateString()}</div>
                    {edit.rejectionReason && (
                      <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: '2px' }}>Reason: {edit.rejectionReason}</div>
                    )}
                  </div>
                  <span style={getStatusStyle(edit.status)}>{edit.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatEditType(type: string): string {
  switch (type) {
    case 'AddCatalogItem': return '➕ Add Catalog Item';
    case 'UpdateCatalogItem': return '✏️ Update Catalog Item';
    case 'RemoveCatalogItem': return '🗑️ Remove Catalog Item';
    case 'UpdateVendorInfo': return '📝 Update Vendor Info';
    default: return type;
  }
}

function getStatusStyle(status: string): React.CSSProperties {
  const base: React.CSSProperties = { fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: '4px' };
  switch (status) {
    case 'Pending': return { ...base, backgroundColor: '#fef3c7', color: '#92400e' };
    case 'Approved': return { ...base, backgroundColor: '#dcfce7', color: '#166534' };
    case 'Rejected': return { ...base, backgroundColor: '#fef2f2', color: '#991b1b' };
    default: return { ...base, backgroundColor: '#f3f4f6', color: '#374151' };
  }
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: '700px', margin: '0 auto', padding: '1rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  loadingBox: { textAlign: 'center', padding: '60px 20px' },
  errorBox: { textAlign: 'center', padding: '60px 20px' },
  backBtn: { marginTop: '16px', padding: '8px 16px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' },
  header: { marginBottom: '1rem' },
  backLink: { background: 'none', border: 'none', color: '#1a6b3c', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', padding: 0, marginBottom: '8px', display: 'block' },
  title: { fontSize: '1.4rem', fontWeight: 800, color: '#0f2d1f', margin: '0 0 4px' },
  subtitle: { fontSize: '0.85rem', color: '#6b7280', margin: 0 },
  pendingBanner: { marginTop: '10px', padding: '8px 12px', backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', fontSize: '0.8rem', color: '#92400e', fontWeight: 500 },
  successBanner: { padding: '10px 14px', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', fontSize: '0.82rem', color: '#166534', fontWeight: 500, marginBottom: '12px', display: 'flex', alignItems: 'center' },
  subNav: { display: 'flex', gap: '0', borderBottom: '2px solid #e5e7eb', marginBottom: '16px' },
  tab: { padding: '10px 16px', background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', fontSize: '0.85rem', fontWeight: 500, color: '#6b7280', cursor: 'pointer' },
  tabActive: { padding: '10px 16px', background: 'none', border: 'none', borderBottom: '2px solid #1a6b3c', marginBottom: '-2px', fontSize: '0.85rem', fontWeight: 700, color: '#1a6b3c', cursor: 'pointer' },
  section: {},
  infoCard: { padding: '16px', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px' },
  infoRow: { display: 'flex', gap: '10px', marginBottom: '8px', fontSize: '0.88rem' },
  infoLabel: { fontWeight: 600, color: '#6b7280', minWidth: '80px', fontSize: '0.8rem' },
  editInfoBtn: { marginTop: '16px', padding: '8px 14px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', color: '#166534', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' },
  formGroup: { marginBottom: '10px' },
  label: { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '4px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.88rem', boxSizing: 'border-box' as const },
  submitBtn: { padding: '10px 18px', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' },
  cancelBtn: { padding: '10px 18px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer' },
  addBtn: { padding: '6px 14px', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' },
  addFormCard: { padding: '16px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', marginBottom: '16px' },
  catalogGrid: { display: 'flex', flexDirection: 'column', gap: '10px' },
  catalogCard: { display: 'flex', gap: '10px', padding: '12px', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', alignItems: 'flex-start' },
  catalogImg: { width: '56px', height: '56px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, backgroundColor: '#f3f4f6' },
  catalogBody: { flex: 1, minWidth: 0 },
  catalogActions: { display: 'flex', flexDirection: 'column', gap: '4px' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.85rem', borderRadius: '4px', lineHeight: 1 },
  historyRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' },
};
